import { useTranslation } from 'react-i18next';

import { Badge } from '@/shared/components/ui/Badge/Badge';
import { RelativeTime } from '@/shared/components/ui/RelativeTime/RelativeTime';
import { cn } from '@/shared/utils/cn/cn.util';

import { type ViolationBehaviorHistoryProps } from './ViolationBehaviorHistory.types';
import { useViolationBehaviorHistoryLogic } from './useViolationBehaviorHistoryLogic';

const HEAD_CLASSES = 'px-sm py-xs text-label font-medium text-muted-foreground';
const CELL_CLASSES = 'px-sm py-xs align-middle';
const NUMBER_CLASSES = 'text-end tabular-nums';

/**
 * The member's pattern, beside the case.
 *
 * A single flag cannot say whether it is a first slip or the fourth this month,
 * and that is the difference between a warning and a suspension. Every rule is
 * a row — a rule with nothing counted is rendered muted, never dropped, because
 * "never" is a finding too — and the case's own rule is emphasised so a reviewer
 * reads it first. A member whose only flag ever is the case itself gets one
 * sentence instead of a table of one and eleven zeros.
 */
export function ViolationBehaviorHistory({ history, currentRule }: ViolationBehaviorHistoryProps) {
  const { t } = useTranslation('violations');
  const { rows, isFirstPattern } = useViolationBehaviorHistoryLogic(history, currentRule);

  return (
    <section className="flex flex-col gap-xs">
      <h3 className="text-caption font-medium text-foreground">{t('review.history.title')}</h3>
      {isFirstPattern ? (
        <p className="text-body text-muted-foreground">{t('review.history.first')}</p>
      ) : (
        // Seven columns do not fit a phone; the table scrolls on its own and the
        // dialog body never does sideways.
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full border-collapse text-body">
            <caption className="sr-only">{t('review.history.title')}</caption>
            <thead className="border-b border-border bg-secondary/40">
              <tr>
                <th scope="col" className={cn(HEAD_CLASSES, 'text-start')}>
                  {t('review.history.column.rule')}
                </th>
                <th scope="col" className={cn(HEAD_CLASSES, NUMBER_CLASSES)}>
                  {t('review.history.column.flags')}
                </th>
                <th scope="col" className={cn(HEAD_CLASSES, NUMBER_CLASSES)}>
                  {t('review.history.column.pending')}
                </th>
                <th scope="col" className={cn(HEAD_CLASSES, NUMBER_CLASSES)}>
                  {t('review.history.column.enforced')}
                </th>
                <th scope="col" className={cn(HEAD_CLASSES, NUMBER_CLASSES)}>
                  {t('review.history.column.dismissed')}
                </th>
                <th scope="col" className={cn(HEAD_CLASSES, NUMBER_CLASSES)}>
                  {t('review.history.column.overturned')}
                </th>
                <th scope="col" className={cn(HEAD_CLASSES, 'text-start')}>
                  {t('review.history.column.last')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.rule}
                  aria-current={row.isCurrent ? 'true' : undefined}
                  className={cn(
                    'border-b border-border last:border-b-0',
                    row.isCurrent && 'bg-accent/10',
                    row.isEmpty && 'text-muted-foreground',
                  )}
                >
                  <th scope="row" className={cn(CELL_CLASSES, 'text-start font-normal')}>
                    <span className="flex flex-wrap items-center gap-xs">
                      <span className={cn(row.isCurrent && 'font-medium text-foreground')}>
                        {t(`behaviorRule.${row.rule}`)}
                      </span>
                      {row.isCurrent ? (
                        <Badge tone="accent">{t('review.history.current')}</Badge>
                      ) : null}
                    </span>
                  </th>
                  <td className={cn(CELL_CLASSES, NUMBER_CLASSES, !row.isEmpty && 'font-medium')}>
                    {row.flags}
                  </td>
                  <td className={cn(CELL_CLASSES, NUMBER_CLASSES)}>{row.pending}</td>
                  <td className={cn(CELL_CLASSES, NUMBER_CLASSES)}>{row.enforced}</td>
                  <td className={cn(CELL_CLASSES, NUMBER_CLASSES)}>{row.dismissed}</td>
                  <td className={cn(CELL_CLASSES, NUMBER_CLASSES)}>{row.overturned}</td>
                  <td className={cn(CELL_CLASSES, 'whitespace-nowrap text-start')}>
                    {row.lastDetectedAt === null ? (
                      t('review.history.never')
                    ) : (
                      <RelativeTime value={row.lastDetectedAt} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-caption text-muted-foreground">{t('review.history.hint')}</p>
    </section>
  );
}
